/**
 * Shared Listings — one page, two scopes.
 *
 * `scope="mine"` (BL Advisor) shows the caller's own shares. `scope="all"`
 * (admin) shows every advisor's, with a "Shared by" column. An org_admin on the
 * admin route gets their own organization's shares instead — still with the
 * "Shared by" column. The backend applies the same split, so this only changes
 * presentation, never trust.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  Check, Copy, Eye, Loader2, Mail, RotateCw, Search, Send, Share2, Users, X,
} from "lucide-react";

import ShareListingModal from "@/components/ShareListingModal";
import { EmptyPanel, MetricTile, PageHero, PagePanel, PageShell, PanelHeading } from "@/components/interior-page";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import Loader from "@/components/ui/loader";
import { useToast } from "@/components/ui/toast";
import { useAuth } from "@/contexts/AuthContext";
import { api, ApiError, assetUrl } from "@/lib/api";
import { isOrgAdmin } from "@/lib/roles";
import { formatDate } from "@/lib/utils";
import type { ListingShare, SharedListingGroup } from "@/types";

const STATUS_STYLES: Record<string, string> = {
  active: "bg-emerald-50 border-emerald-200 text-emerald-800",
  revoked: "bg-slate-100 border-slate-200 text-slate-500",
  expired: "bg-amber-50 border-amber-200 text-amber-800",
};

export default function SharedListingsPage({ scope = "mine" }: { scope?: "mine" | "all" }) {
  const toast = useToast();
  const { user } = useAuth();
  const orgView = scope === "all" && isOrgAdmin(user);
  const [groups, setGroups] = useState<SharedListingGroup[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [busyId, setBusyId] = useState<string | null>(null);
  const [copiedId, setCopiedId] = useState<string | null>(null);
  const [modal, setModal] = useState<{ id: string; title: string } | null>(null);

  const load = useCallback(() => {
    api
      .get<SharedListingGroup[]>("/listing-shares/")
      .then(setGroups)
      .catch(() => toast.error("Couldn't load your shared listings."))
      .finally(() => setLoading(false));
  }, [toast]);

  useEffect(() => load(), [load]);

  const totals = useMemo(() => {
    const shares = groups.flatMap((g) => g.shares);
    return {
      listings: groups.length,
      recipients: new Set(shares.filter((s) => s.status === "active").map((s) => s.recipient_email)).size,
      active: shares.filter((s) => s.status === "active").length,
      views: shares.reduce((sum, s) => sum + s.view_count, 0),
    };
  }, [groups]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return groups;
    return groups
      .map((g) => {
        const matchesListing =
          g.title.toLowerCase().includes(q) || (g.reference_code || "").toLowerCase().includes(q);
        if (matchesListing) return g;
        const shares = g.shares.filter(
          (s) =>
            s.recipient_email.toLowerCase().includes(q) ||
            (s.recipient_name || "").toLowerCase().includes(q) ||
            (s.advisor_name || "").toLowerCase().includes(q),
        );
        return shares.length > 0 ? { ...g, shares } : null;
      })
      .filter((g): g is SharedListingGroup => g !== null);
  }, [groups, search]);

  async function revoke(share: ListingShare) {
    setBusyId(share.id);
    try {
      await api.delete(`/listing-shares/${share.id}`);
      toast.success(`Access removed for ${share.recipient_email}`);
      load();
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : "Couldn't remove this recipient.");
    } finally {
      setBusyId(null);
    }
  }

  async function resend(share: ListingShare) {
    setBusyId(share.id);
    try {
      await api.post<ListingShare>(`/listing-shares/${share.id}/resend`, {});
      toast.success(`Link re-sent to ${share.recipient_email}`);
      load();
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : "Couldn't resend this link.");
    } finally {
      setBusyId(null);
    }
  }

  async function copy(share: ListingShare) {
    try {
      await navigator.clipboard.writeText(share.share_url);
      setCopiedId(share.id);
      setTimeout(() => setCopiedId(null), 2000);
    } catch {
      toast.error("Could not copy the link.");
    }
  }

  if (loading) return <Loader className="min-h-[60vh]" />;

  return (
    <PageShell>
      <PageHero
        eyebrow="Shared Listings"
        title={orgView ? "Listings your organization has shared" : scope === "all" ? "Every listing shared with a guest" : "Listings you've shared"}
        description={
          orgView
            ? "Every share created by your organization's BL Advisors and admins. Remove a recipient to invalidate their link immediately."
            : scope === "all"
            ? "Every share created by any BL Advisor. Remove a recipient to invalidate their link immediately."
            : "Listings you've shared with people outside BusinessLocating. Remove a recipient and their link stops working straight away."
        }
        aside={
          <div className="flex items-center gap-3">
            <div className="flex h-11 w-11 items-center justify-center rounded-lg bg-primary/10 text-primary">
              <Users className="h-5 w-5" />
            </div>
            <div>
              <div className="text-2xl font-extrabold tracking-tight text-foreground">{totals.recipients}</div>
              <div className="text-[0.68rem] font-bold uppercase tracking-[0.16em] text-muted-foreground">
                Active recipients
              </div>
            </div>
          </div>
        }
      />

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <MetricTile label="Listings shared" value={totals.listings} icon={Share2} />
        <MetricTile label="Active links" value={totals.active} icon={Mail} tone="emerald" />
        <MetricTile label="Recipients" value={totals.recipients} icon={Users} tone="amber" />
        <MetricTile label="Total views" value={totals.views} icon={Eye} />
      </div>

      <PagePanel>
        <PanelHeading
          title="Shares by listing"
          description="Each bubble is one recipient. The x removes their access instantly."
          actions={
            <div className="relative w-full sm:w-72">
              <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder={scope === "all" ? "Search listing, recipient or advisor" : "Search listing or recipient"}
                className="pl-9"
              />
            </div>
          }
        />

        {filtered.length === 0 ? (
          <EmptyPanel
            icon={Share2}
            title={search ? "No matches" : "Nothing shared yet"}
            description={
              search
                ? "No listing or recipient matches that search."
                : "Open any active listing and use Share to send it to someone outside BusinessLocating."
            }
          />
        ) : (
          <div className="space-y-4">
            {filtered.map((group) => {
              const image = assetUrl(group.listing_image_url);
              const unavailable = group.is_deleted || group.listing_status_id !== 3;
              return (
                <div key={group.listing_id} className="rounded-xl border border-border/75 bg-card p-4">
                  <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                    <div className="flex min-w-0 gap-3">
                      <div className="h-14 w-20 shrink-0 overflow-hidden rounded-lg bg-muted/50">
                        {image && <img src={image} alt="" className="h-full w-full object-cover" />}
                      </div>
                      <div className="min-w-0">
                        <Link
                          to={`/listings/${group.listing_id}`}
                          className="line-clamp-1 font-bold text-foreground hover:text-primary"
                        >
                          {group.title}
                        </Link>
                        <div className="mt-0.5 flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-muted-foreground">
                          {group.reference_code && <span className="font-mono">{group.reference_code}</span>}
                          {group.category_name && <span>· {group.category_name}</span>}
                          {(group.city_name || group.state_name) && (
                            <span>· {[group.city_name, group.state_name].filter(Boolean).join(", ")}</span>
                          )}
                          {unavailable && (
                            <span className="rounded-full bg-amber-100 px-2 py-0.5 font-bold text-amber-700">
                              Not live — recipients see &ldquo;no longer available&rdquo;
                            </span>
                          )}
                        </div>
                      </div>
                    </div>

                    {!unavailable && (
                      <Button
                        size="sm"
                        variant="outline"
                        className="shrink-0"
                        onClick={() => setModal({ id: group.listing_id, title: group.title })}
                      >
                        <Send className="h-4 w-4" />
                        Share again
                      </Button>
                    )}
                  </div>

                  <ul className="mt-3 flex flex-wrap gap-2 border-t border-border/50 pt-3">
                    {group.shares.map((s) => (
                      <li
                        key={s.id}
                        className={`flex max-w-full items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs font-semibold ${
                          STATUS_STYLES[s.status] || STATUS_STYLES.revoked
                        }`}
                        title={
                          [
                            `Shared ${formatDate(s.created_at)}`,
                            s.last_viewed_at ? `last viewed ${formatDate(s.last_viewed_at)}` : "never viewed",
                            s.advisor_name ? `by ${s.advisor_name}` : "",
                          ]
                            .filter(Boolean)
                            .join(" · ")
                        }
                      >
                        <Mail className="h-3.5 w-3.5 shrink-0 opacity-60" />
                        <span className="truncate">
                          {s.recipient_name ? `${s.recipient_name} · ` : ""}
                          {s.recipient_email}
                        </span>
                        {scope === "all" && s.advisor_name && (
                          <span className="shrink-0 rounded-full bg-white/70 px-1.5 py-0.5 text-[0.62rem] font-bold">
                            {s.advisor_name}
                          </span>
                        )}
                        {s.view_count > 0 && (
                          <span className="inline-flex shrink-0 items-center gap-0.5 rounded-full bg-white/70 px-1.5 py-0.5 text-[0.62rem] font-bold">
                            <Eye className="h-3 w-3" />
                            {s.view_count}
                          </span>
                        )}

                        {busyId === s.id ? (
                          <Loader2 className="h-3.5 w-3.5 shrink-0 animate-spin" />
                        ) : s.status === "active" ? (
                          <>
                            <button
                              onClick={() => copy(s)}
                              className="shrink-0 rounded-full p-0.5 opacity-60 hover:opacity-100"
                              aria-label={`Copy link for ${s.recipient_email}`}
                              title="Copy link"
                            >
                              {copiedId === s.id ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
                            </button>
                            <button
                              onClick={() => revoke(s)}
                              className="shrink-0 rounded-full p-0.5 opacity-60 hover:bg-black/10 hover:opacity-100"
                              aria-label={`Remove access for ${s.recipient_email}`}
                              title="Remove access — their link stops working immediately"
                            >
                              <X className="h-3.5 w-3.5" />
                            </button>
                          </>
                        ) : (
                          !unavailable && (
                            <button
                              onClick={() => resend(s)}
                              className="shrink-0 rounded-full p-0.5 opacity-60 hover:opacity-100"
                              aria-label={`Re-share with ${s.recipient_email}`}
                              title="Share again"
                            >
                              <RotateCw className="h-3.5 w-3.5" />
                            </button>
                          )
                        )}
                      </li>
                    ))}
                  </ul>
                </div>
              );
            })}
          </div>
        )}
      </PagePanel>

      {modal && (
        <ShareListingModal
          listingId={modal.id}
          listingTitle={modal.title}
          onClose={() => setModal(null)}
          onChanged={load}
        />
      )}
    </PageShell>
  );
}
